import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  PackageSearchIcon,
  ServerCrashIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CreateOrderGroupActionForm } from "~/components/common/group-actions/create-order-group-action-form";
import {
  DEFAULT_COLUMN_VISIBILITY,
  OrderList,
  SELECTABLE_TABLE_DEFAULT_STATE,
} from "~/components/common/orders/order-list";
import type { Orders_Search_Input } from "~/graphql/graphql.gen";
import {
  useCurrentUserQuery,
  useInfiniteSearchOrdersQuery,
  useSearchOrdersQuery,
} from "~/graphql/graphql.gen";
import {
  queryParamsToGraphQLFilters,
  queryParamsToGraphQLSorting,
  queryParamsToTableColumnVisibility,
} from "~/stores/orders-store";
import { AGENT_OR_MARKETING_ROLES } from "~/stores/roles-store";
import { createGroupActionQueryParamsSchema } from "~/validators/create-group-action-validator";

const ORDER_LIMIT = 500;

export const Route = createFileRoute("/_auth/_app_shell/create-group-action")({
  component: RouteComponent,
  validateSearch: (search) => createGroupActionQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();

  return (
    <>
      <title>{`${t("create_group_action.title")} - ${t("app_name")}`}</title>
      <div className="h-svh w-full overflow-hidden">
        <div className="bg-container flex h-full w-full overflow-hidden">
          <div className="bg-background flex w-full flex-col overflow-hidden">
            <OrderView />
          </div>
        </div>
      </div>
    </>
  );
}

function OrderView() {
  const { t } = useTranslation();

  const search = Route.useSearch();

  const [isPending, setIsPending] = useState(false);

  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });

  const query: Orders_Search_Input = {
    filter: queryParamsToGraphQLFilters(search),
    sort: queryParamsToGraphQLSorting(search),
    pageInfo: {
      first: 1,
    },
  };

  const { data } = useSearchOrdersQuery({
    input: query,
  });

  const hasAgentOrMarketingRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_OR_MARKETING_ROLES.includes(user.internalUser.supportRole);

  return (
    <div className="flex h-full grow flex-col">
      <header className="flex flex-col items-center">
        <div className="flex h-14 w-full items-center justify-between gap-2 border-b px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              nativeButton={false}
              render={
                <Link to="/group-actions">
                  <ArrowLeftIcon />
                </Link>
              }
            />

            <h2 className="text-sm">{t("create_group_action.title")}</h2>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div>
              {data ? (
                data.orders_search.totalCount > ORDER_LIMIT ? (
                  <div className="flex items-center gap-2">
                    <TriangleAlertIcon className="size-4 text-yellow-600" />
                    {t("create_group_action.x_order_limit", {
                      count: ORDER_LIMIT,
                    })}
                  </div>
                ) : (
                  t("create_group_action.x_order", {
                    count: data.orders_search.totalCount,
                  })
                )
              ) : null}
            </div>
            <Button
              type="submit"
              size="sm"
              form="create-group-action-form"
              disabled={
                !hasAgentOrMarketingRights ||
                !data ||
                data.orders_search.totalCount === 0 ||
                isPending
              }
            >
              {isPending && (
                <>
                  <Spinner aria-hidden="true" />
                  {t("create_group_action.create_loading")}
                </>
              )}
              {!isPending && t("create_group_action.create")}
            </Button>
          </div>
        </div>
      </header>
      <div className="grid h-full w-full grid-cols-[1fr_556px] overflow-hidden">
        <div className="h-full w-full overflow-auto">
          <Orders />
        </div>

        <div className="overflow-auto border-l px-10 py-6">
          <CreateOrderGroupActionForm
            totalCount={data?.orders_search.totalCount ?? 0}
            setIsPending={setIsPending}
          />
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const { t } = useTranslation();
  const search = Route.useSearch();

  const query: Orders_Search_Input = {
    filter: queryParamsToGraphQLFilters(search),
    sort: queryParamsToGraphQLSorting(search),
    pageInfo: {
      first: 50,
    },
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteSearchOrdersQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.orders_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                pageInfo: {
                  ...query.pageInfo,
                  after: lastPage.orders_search.pageInfo.endCursor,
                },
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.orders_search),
    },
  );

  const columnVisibility = queryParamsToTableColumnVisibility(
    search,
    DEFAULT_COLUMN_VISIBILITY,
  );

  const orders =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("orders.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("orders.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <OrderList
      data={orders}
      state={{
        ...SELECTABLE_TABLE_DEFAULT_STATE,
        columnVisibility: {
          ...columnVisibility,
          select: false,
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
